import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { Program, ProgramSection } from '../../types/program';
import { insideWord, plain, resourceWord } from '../../utils/cardFacts';
import { programHubHref } from '../../utils/hubChapter';
import { sectionAnchors } from '../../utils/headingToc';
import { placePath } from '../../utils/placePaths';
import { openPlace, usePlaceOrigin } from '../../utils/placeMotion';

interface ProgramCardProps {
  program: Pick<Program, 'id' | 'title' | 'slug' | 'introduction' | 'hubs' | 'kind'> & {
    sections?: Program['sections'];
    highlights?: Program['highlights'];
  };
  query?: string;
  anchorId?: string;
  highlighted?: boolean;
}

function sectionIdsUnder(sections: ProgramSection[], sectionId: string): Set<string> {
  const ids = new Set<string>([sectionId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const section of sections) {
      if (section.parentId && ids.has(section.parentId) && !ids.has(section.id)) {
        ids.add(section.id);
        grew = true;
      }
    }
  }
  return ids;
}

function HubRow({
  programSlug,
  hub,
}: {
  programSlug: string;
  hub: Program['hubs'][number];
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const to = programHubHref(programSlug, hub.slug);
  usePlaceOrigin(ref, 'hub', hub.slug, 'row');
  return (
    <li className="border-b border-gray-200">
      <Link
        ref={ref}
        to={to}
        onClick={(event) => {
          openPlace(event, ref.current, { kind: 'hub', slug: hub.slug, via: 'row' }, to, navigate, location);
        }}
        className="flex items-center w-full min-h-[44px] text-left focus-ring"
      >
        <span className="inline-flex flex-wrap items-baseline gap-x-3 min-w-0">
          <span className="text-sm font-medium text-gray-900">{hub.title}</span>
          <span className="text-sm text-gray-600">{resourceWord(hub.resources.length)}</span>
        </span>
      </Link>
    </li>
  );
}

function SectionRow({
  title,
  count,
  to,
}: {
  title: string;
  count: number;
  to: string;
}) {
  return (
    <li className="border-b border-gray-200">
      <Link to={to} className="flex items-center w-full min-h-[44px] text-left focus-ring">
        <span className="inline-flex flex-wrap items-baseline gap-x-3 min-w-0">
          <span className="text-sm font-medium text-gray-900">{title}</span>
          {count > 0 && <span className="text-sm text-gray-600">{resourceWord(count)}</span>}
        </span>
      </Link>
    </li>
  );
}

export default function ProgramCard({ program, query = '', anchorId, highlighted = false }: ProgramCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState<boolean | null>(null);
  const sections = (program.sections ?? []).filter((section) => !section.parentId);
  const highlights = program.highlights ?? [];
  const anchors = sectionAnchors(
    [...(program.sections ?? [])].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
  );
  const rows: Array<
    | { key: string; kind: 'hub'; hub: Program['hubs'][number]; title: string }
    | { key: string; kind: 'section'; title: string; count: number; to: string }
  > = [
    ...[...program.hubs].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)).map((hub) => ({
      key: `hub-${hub.id}`,
      kind: 'hub' as const,
      hub,
      title: hub.title,
    })),
    ...[...sections].sort((a, b) => a.order - b.order).map((section) => {
      const ids = sectionIdsUnder(program.sections ?? [], section.id);
      const count = highlights.filter((highlight) => highlight.sectionId && ids.has(highlight.sectionId)).length;
      const to = section.slug
        ? `${placePath(program)}/${section.slug}`
        : `${placePath(program)}#${anchors.get(section.id)}`;
      return {
        key: `section-${section.id}`,
        kind: 'section' as const,
        title: section.title,
        count,
        to,
      };
    }),
  ];
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? rows.filter((row) => row.title.toLowerCase().includes(needle))
    : [];
  const listOpen = opened ?? matches.length > 0;
  const listed = opened === true || matches.length === 0 ? rows : matches;

  useEffect(() => {
    setOpened(null);
  }, [needle]);
  const introduction = plain(program.introduction);
  const to = placePath(program);
  usePlaceOrigin(cardRef, 'program', program.slug);
  const label = insideWord(sections.length, program.hubs.length);

  const renderRow = (row: (typeof rows)[number]) =>
    row.kind === 'hub' ? (
      <HubRow key={row.key} programSlug={program.slug} hub={row.hub} />
    ) : (
      <SectionRow key={row.key} title={row.title} count={row.count} to={row.to} />
    );

  return (
    <div id={anchorId} className={anchorId ? 'scroll-mt-36' : undefined}>
    <Card ref={cardRef} className={`!p-4 ${highlighted ? '!bg-start-wash !border-l-4 !border-l-start-edge' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-600">{program.kind === 'REFERENCE' ? 'Reference' : 'Program'}</p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{program.title}</h3>
        </div>
        <SaveToMyOrbconButton
          type="PROGRAM"
          programId={program.id}
          title={program.title}
          slug={program.slug}
          variant="icon"
          align="end"
        />
      </div>
      {introduction && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{introduction}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        {rows.length > 0 && (
          <button
            type="button"
            className="min-h-[44px] text-sm font-medium text-primary focus-ring rounded-sm"
            aria-expanded={listOpen}
            onClick={() => setOpened(listOpen ? false : true)}
          >
            {listOpen ? 'Hide sections' : label}
          </button>
        )}
        <span className="inline-flex min-h-[44px] items-center">
          <QuietAction
            to={to}
            label={`Open ${program.title}`}
            onClick={(event) => {
              openPlace(event, cardRef.current, { kind: 'program', slug: program.slug }, to, navigate, location);
            }}
          >
            Open
          </QuietAction>
        </span>
      </div>
      {listOpen && (
        <ul className="mt-2 border-t border-gray-200">
          {listed.map((row) => renderRow(row))}
        </ul>
      )}
    </Card>
    </div>
  );
}
